import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Briefcase, Calendar, Code2, Edit2, FolderKanban, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@radix-ui/react-popover";
import { format } from "date-fns";

interface PlatformProjectsProps {
  projects: any;
  accessToken: string | null;
  freelancerId?: string | null;
}

export const PlatformProjects = ({
  projects,
}: PlatformProjectsProps) => {

  return (
    <Card className="hover:shadow-md transition-all duration-300 border-green-100 bg-gradient-to-br from-white to-green-50/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-400 to-emerald-400 flex items-center justify-center shadow-inner">
            <FolderKanban className="w-5 h-5 text-white" />
          </div>
          <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
            Platorm Project
          </span>
        </CardTitle>
      </CardHeader>

      <CardContent>
        {projects?.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-gradient-to-br from-green-50/50 to-emerald-50/50 rounded-lg border border-dashed border-green-200 p-6">
            <p className="text-emerald-500 font-medium">
              No projects to display.
            </p>
          </div>
        ) : (
          <div className="relative pl-8 space-y-0 pt-2">
            <div className="absolute left-3.5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-300 to-emerald-100"></div>

            {projects?.map((project, index) => (
              <div
                key={index}
                className="relative pb-8 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute left-[-27px] w-6 h-6 rounded-full bg-emerald-50 border flex items-center justify-center shadow-sm z-10">
                  <Code2 className="w-4 h-4 text-green-500" />
                </div>

                <div className="ml-2 p-4 rounded-lg bg-green-50 border-green-100 shadow-sm hover:shadow transition-all">
                  <div className="flex justify-between">
                    <p className="text-gray-800 font-semibold">
                      {project?.name ? project?.name : ""}
                    </p>
                    <p className="text-gray-800 font-semibold">
                      <div className="flex flex-row gap-1">
                        <Star className={`h-4 w-4 transition-colors fill-yellow-400 text-yellow-400`} />
                        <span className="text-xs" >{Number(project?.rating ?? project?.averageRating)}/5</span>
                        {!!project?.review && <Popover>
                          <PopoverTrigger asChild>
                            <span className="text-xs cursor-pointer text-blue-600 hover:underline">Review</span>
                          </PopoverTrigger>
                          <PopoverContent className="w-80 space-y-3 bg-white border border-gray-300 shadow-lg rounded-md p-4 z-50">
                            <p className="text-sm text-gray-800">{project.review}</p>
                          </PopoverContent>
                        </Popover>}
                      </div>
                    </p>
                  </div>
                  <p className="text-gray-600">{project?.description ? project?.description : ""}</p>

                  <div className="flex flex-wrap gap-2 mt-2">
                    {project.skills.map((tech, i) => (
                      <Badge
                        key={i}
                        className="bg-emerald-100 text-emerald-800"
                      >
                        {tech}
                      </Badge>
                    ))}
                  </div>

                  {<div className="text-sm mt-2 text-gray-600">
                    <strong>Total Spent:</strong> {project.totalSpent ?? 0}
                  </div>}

                  {project?.startDate && (
                      <div className="flex items-center gap-1 text-xs mt-1">
                        <Calendar className="w-3.5 h-3.5 text-gray-500" />
                        <span>
                          {format(
                            new Date(project.startDate),
                            "MMM yyyy"
                          )}{" "}
                          -{" "}
                          {project.endDate
                            ? format(
                                new Date(project.endDate),
                                "MMM yyyy"
                              )
                            : "Present"}
                        </span>
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};